// Provider-facing panel shown during an active chat/voice session.
// Polls open in-session requests (extensions + therapy bookings) and lets the
// counsellor EXPLICITLY accept before any payment request is created.
//   GET  /api/pricing/session/{id}/in-session-requests
//   POST /api/pricing/extension/approve
//   POST /api/pricing/in-session-booking/approve
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Clock, CalendarPlus, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function InSessionRequests({ sessionId, user }) {
  const [extensions, setExtensions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API}/pricing/session/${sessionId}/in-session-requests`);
      setExtensions((res.data.extensions || []).filter((e) => e.status === 'pending'));
      setBookings((res.data.bookings || []).filter((b) => b.status === 'pending'));
    } catch (_) { /* keep last state */ }
  }, [sessionId]);

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 4000);
    return () => clearInterval(iv);
  }, [refresh]);

  const acceptExtension = async (ext) => {
    setBusy(ext.id);
    try {
      await axios.post(`${API}/pricing/extension/approve`,
        { extension_request_id: ext.id },
        { params: { approver_user_id: user.id } });
      toast.success('Extension accepted — care-seeker can now pay');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept');
    } finally { setBusy(null); }
  };

  const acceptBooking = async (b) => {
    setBusy(b.id);
    try {
      await axios.post(`${API}/pricing/in-session-booking/approve`, null,
        { params: { booking_id: b.id, approver_user_id: user.id } });
      toast.success('Booking accepted — care-seeker can now pay');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept');
    } finally { setBusy(null); }
  };

  if (extensions.length === 0 && bookings.length === 0) return null;

  return (
    <div className="card p-4 border-amber-200 bg-amber-50" data-testid="in-session-requests">
      <h4 className="font-medium text-amber-900 mb-3 text-sm">Care-seeker requests</h4>
      <div className="space-y-2">
        {extensions.map((ext) => (
          <div key={ext.id} className="flex items-center justify-between gap-3 bg-white rounded-lg p-3"
               data-testid={`pending-extension-${ext.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <Clock size={18} className="text-amber-600 shrink-0" />
              <span className="text-sm text-gray-700 truncate">
                Extend by +{ext.extension_minutes} min · ₹{ext.price_inr}
              </span>
            </div>
            <button
              onClick={() => acceptExtension(ext)}
              disabled={busy === ext.id}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm hover:opacity-90 disabled:opacity-50"
              data-testid={`accept-extension-${ext.id}`}
            >
              {busy === ext.id ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Accept
            </button>
          </div>
        ))}
        {bookings.map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-3 bg-white rounded-lg p-3"
               data-testid={`pending-booking-${b.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <CalendarPlus size={18} className="text-amber-600 shrink-0" />
              <span className="text-sm text-gray-700 truncate">
                Book {b.grade_name || b.grade_code} · ₹{b.price_inr}
              </span>
            </div>
            <button
              onClick={() => acceptBooking(b)}
              disabled={busy === b.id}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm hover:opacity-90 disabled:opacity-50"
              data-testid={`accept-booking-${b.id}`}
            >
              {busy === b.id ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Accept
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
